import React, { useEffect, useMemo } from 'react';
import './PrivacyPolicyPage.scss';
import { useTranslation } from 'react-i18next';
import LandingHeader from '../LandingPage/LandingHeader';
import Footer from '../LandingPage/Footer';
import CookieConsent from '../LandingPage/CookieConsent';
import { trackPageView } from '../../analytics';
import { parseLegalContent } from '../../utils/legalContent';

const PRIVACY_CONTENT_EN = `## 1.1 Introduction

This Privacy Policy explains how FEEL IT SERVICES SAS (and its subsidiaries) ("we", "us", "our") collects, uses, shares, and protects information in connection with the Artefact mobile application (the "App"), available on the Apple App Store and Google Play Store. By downloading, accessing, or using the App, you agree to the collection and use of information in accordance with this Privacy Policy.

If you do not agree with this Privacy Policy, please do not use the App.

## 1.2 Who We Are (Data Controller)

The data controller responsible for your personal data is:

**FEEL IT SERVICES SAS (and its subsidiaries)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Phone: +33 6 62 88 36 50
Email: connect@feel-it-services.com

## 1.3 Information We Collect

We collect the following categories of information:

### a) Account Information

When you create an account in the App, we may collect your name, email address, and password (encrypted), or, if you choose to sign in via a third-party provider, an identifier and basic profile information from that provider.

Account creation is available via email and password, or via sign-in with Google or Apple.

### b) Camera and Photo Data

The core feature of the App allows you to scan museum exhibits and artworks using your device's camera. Images captured or uploaded for this purpose are processed to identify the artwork or exhibit and to provide you with related information (descriptions, historical facts, itineraries, etc.). Camera access is requested only when you use the scanning feature and can be managed at any time through your device settings.

### c) Location Data

With your permission, we may collect approximate or precise location data to help you find nearby museums, exhibitions, and points of interest, and to personalize your visit itinerary.

Location data is collected only while the App is in use (foreground) and is not tracked in the background.

### d) Usage and Analytics Data

We use third-party analytics services to understand how the App is used (e.g., screens visited, features used, session duration, crash reports) so that we can improve the App's performance and user experience. This data is generally aggregated or pseudonymized.

### e) Device Information

We may automatically collect certain technical information, such as device model, operating system and version, unique device identifiers, language settings, and mobile network information.

## 1.4 Artificial Intelligence (AI) Features

The App uses an artificial intelligence service provided by a third party to recognize and identify museum exhibits and artworks from images you capture. This AI recognition process analyzes the visual content of the image (the artwork itself) to return identification and informational results.

Important: The AI recognition provider does not receive, process, or store any personal data about you. Only the image content necessary for object/artwork recognition is transmitted for processing, and it is not linked to your identity, account, or any other personal identifier by the AI provider.

## 1.5 Legal Basis for Processing (EU/EEA Users)

Where the GDPR applies, we process your personal data on the following legal bases:

- Performance of a contract — to provide you with the App's core functionality (account, scanning, itineraries).
- Consent — for camera access, precise location, and optional features, which you can withdraw at any time.
- Legitimate interests — for analytics, security, fraud prevention, and improving the App, balanced against your rights.
- Legal obligation — where processing is required to comply with applicable law.

## 1.6 How We Share Your Information

We do not sell your personal data. We may share information with:

- Service providers / processors who perform services on our behalf, such as cloud hosting, analytics, and AI-based image recognition, under contractual confidentiality and data protection obligations.
- Museums or cultural institutions, only where necessary to deliver exhibit content, and typically in aggregated or non-identifiable form.
- Legal and regulatory authorities, where required by law, regulation, or legal process.
- In connection with a corporate transaction (merger, acquisition, restructuring), subject to confidentiality safeguards.

The specific service providers we use are: Google Analytics and Mixpanel for usage and analytics data, and OpenAI for AI-based artwork and exhibit recognition.

## 1.7 Data Retention

We retain personal data only for as long as necessary to fulfill the purposes described in this Privacy Policy, comply with legal obligations, resolve disputes, and enforce our agreements.

Specifically, we retain user data for a period of 1 year, after which it is deleted or anonymized, unless a longer retention period is required to comply with a legal obligation.

## 1.8 Data Security

We implement appropriate technical and organizational measures designed to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.

## 1.9 Children's Privacy

The App is not directed at children under the age of 16. We do not knowingly collect personal data from children under 16. If you believe a child has provided us with personal data, please contact us so we can delete it.

## 1.10 International Data Transfers

As the App is available internationally, your data may be transferred to and processed in countries outside your country of residence, including outside the European Economic Area (EEA), such as the United States or the United Kingdom. Where such transfers occur, we rely on appropriate safeguards, such as the European Commission's Standard Contractual Clauses (SCCs) or adequacy decisions, to protect your personal data.

## 1.11 Your Rights

Depending on your location, you may have rights regarding your personal data, including the right to access, correct, delete, restrict, or port your data, and to object to certain processing. Please see Section 3 (GDPR Compliance Statement) for full details on how to exercise these rights.

## 1.12 Changes to This Privacy Policy

We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy in the App or on our website, with a revised "Last updated" date. Continued use of the App after changes take effect constitutes acceptance of the revised policy.

## 1.13 Contact Us

If you have questions or concerns about this Privacy Policy or our data practices, please contact us:

**FEEL IT SERVICES SAS (and its subsidiaries)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Phone: +33 6 62 88 36 50
Email: connect@feel-it-services.com`;

const PRIVACY_CONTENT_RO = `## 1.1 Introducere

Această Politică de Confidențialitate explică modul în care FEEL IT SERVICES SAS (și filialele sale) ("noi", "ne", "al nostru") colectează, utilizează, distribuie și protejează informațiile în legătură cu aplicația mobilă Artefact ("Aplicația"), disponibilă pe Apple App Store și Google Play Store. Prin descărcarea, accesarea sau utilizarea Aplicației, sunteți de acord cu colectarea și utilizarea informațiilor în conformitate cu această Politică de Confidențialitate.

Dacă nu sunteți de acord cu această Politică de Confidențialitate, vă rugăm să nu utilizați Aplicația.

## 1.2 Cine suntem (Operatorul de date)

Operatorul de date responsabil pentru datele dumneavoastră cu caracter personal este:

**FEEL IT SERVICES SAS (și filialele sale)**
13bis Avenue de la Motte Picquet, 75007 Paris, Franța
RCS Paris B 531 361 459
Telefon: +33 6 62 88 36 50
Email: connect@feel-it-services.com

## 1.3 Informațiile pe care le colectăm

Colectăm următoarele categorii de informații:

### a) Informații despre cont

Atunci când vă creați un cont în Aplicație, este posibil să colectăm numele, adresa de email și parola (criptată) sau, dacă alegeți să vă conectați printr-un furnizor terț, un identificator și informații de bază despre profil de la acel furnizor.

Crearea unui cont este disponibilă prin email și parolă sau prin conectare cu Google sau Apple.

### b) Date privind camera și fotografiile

Funcția principală a Aplicației vă permite să scanați exponate și opere de artă din muzee folosind camera dispozitivului dumneavoastră. Imaginile capturate sau încărcate în acest scop sunt procesate pentru a identifica opera de artă sau exponatul și pentru a vă oferi informații conexe (descrieri, fapte istorice, itinerarii etc.). Accesul la cameră este solicitat doar atunci când utilizați funcția de scanare și poate fi gestionat oricând din setările dispozitivului dumneavoastră.

### c) Date de localizare

Cu permisiunea dumneavoastră, este posibil să colectăm date de localizare aproximative sau precise pentru a vă ajuta să găsiți muzee, expoziții și puncte de interes din apropiere și pentru a vă personaliza itinerarul de vizită.

Datele de localizare sunt colectate doar cât timp Aplicația este utilizată activ (în prim-plan) și nu sunt urmărite în fundal.

### d) Date de utilizare și analiză

Utilizăm servicii de analiză terțe pentru a înțelege modul în care este utilizată Aplicația (de exemplu, ecranele vizitate, funcțiile utilizate, durata sesiunii, rapoartele de erori), astfel încât să putem îmbunătăți performanța și experiența utilizatorului. Aceste date sunt, în general, agregate sau pseudonimizate.

### e) Informații despre dispozitiv

Este posibil să colectăm automat anumite informații tehnice, precum modelul dispozitivului, sistemul de operare și versiunea acestuia, identificatori unici ai dispozitivului, setările de limbă și informații despre rețeaua mobilă.

## 1.4 Funcții de inteligență artificială (IA)

Aplicația utilizează un serviciu de inteligență artificială furnizat de un terț pentru a recunoaște și identifica exponate și opere de artă din muzee pe baza imaginilor capturate. Acest proces de recunoaștere prin IA analizează conținutul vizual al imaginii (opera de artă în sine) pentru a returna rezultate de identificare și informare.

Important: Furnizorul serviciului de recunoaștere prin IA nu primește, nu procesează și nu stochează nicio dată cu caracter personal despre dumneavoastră. Doar conținutul imaginii necesar pentru recunoașterea obiectului/operei de artă este transmis pentru procesare și nu este asociat cu identitatea, contul sau orice alt identificator personal al dumneavoastră de către furnizorul de IA.

## 1.5 Temeiul juridic al prelucrării (utilizatori din UE/SEE)

Acolo unde se aplică RGPD, prelucrăm datele dumneavoastră cu caracter personal pe baza următoarelor temeiuri juridice:

- Executarea unui contract — pentru a vă oferi funcționalitățile de bază ale Aplicației (cont, scanare, itinerarii).
- Consimțământ — pentru accesul la cameră, localizarea precisă și funcțiile opționale, pe care le puteți retrage oricând.
- Interese legitime — pentru analiză, securitate, prevenirea fraudei și îmbunătățirea Aplicației, echilibrate cu drepturile dumneavoastră.
- Obligație legală — atunci când prelucrarea este necesară pentru respectarea legislației aplicabile.

## 1.6 Cum partajăm informațiile dumneavoastră

Nu vindem datele dumneavoastră cu caracter personal. Este posibil să partajăm informații cu:

- Furnizori de servicii / persoane împuternicite care prestează servicii în numele nostru, precum găzduire cloud, analiză și recunoaștere a imaginilor bazată pe IA, sub obligații contractuale de confidențialitate și protecție a datelor.
- Muzee sau instituții culturale, doar acolo unde este necesar pentru a livra conținutul exponatelor, de regulă sub formă agregată sau neidentificabilă.
- Autorități legale și de reglementare, atunci când este cerut de lege, reglementare sau proces legal.
- În legătură cu o tranzacție corporativă (fuziune, achiziție, restructurare), sub rezerva unor garanții de confidențialitate.

Furnizorii de servicii specifici pe care îi folosim sunt: Google Analytics și Mixpanel pentru date de utilizare și analiză, și OpenAI pentru recunoașterea prin IA a operelor de artă și exponatelor.

## 1.7 Păstrarea datelor

Păstrăm datele cu caracter personal doar atât timp cât este necesar pentru a îndeplini scopurile descrise în această Politică de Confidențialitate, pentru a respecta obligațiile legale, pentru a soluționa litigii și pentru a ne pune în aplicare acordurile.

Mai exact, păstrăm datele utilizatorilor pentru o perioadă de 1 an, după care sunt șterse sau anonimizate, cu excepția cazului în care este necesară o perioadă de păstrare mai lungă pentru respectarea unei obligații legale.

## 1.8 Securitatea datelor

Implementăm măsuri tehnice și organizatorice adecvate, concepute pentru a proteja datele dumneavoastră cu caracter personal împotriva accesului neautorizat, alterării, divulgării sau distrugerii. Cu toate acestea, nicio metodă de transmisie prin internet sau de stocare electronică nu este 100% sigură, iar noi nu putem garanta o securitate absolută.

## 1.9 Confidențialitatea copiilor

Aplicația nu se adresează copiilor sub 16 ani. Nu colectăm cu bună știință date cu caracter personal de la copii sub 16 ani. Dacă considerați că un copil ne-a furnizat date cu caracter personal, vă rugăm să ne contactați pentru a le putea șterge.

## 1.10 Transferuri internaționale de date

Deoarece Aplicația este disponibilă la nivel internațional, datele dumneavoastră pot fi transferate și procesate în țări din afara țării dumneavoastră de reședință, inclusiv din afara Spațiului Economic European (SEE), precum Statele Unite sau Regatul Unit. Acolo unde au loc astfel de transferuri, ne bazăm pe garanții adecvate, precum Clauzele Contractuale Standard (SCC) ale Comisiei Europene sau deciziile de adecvare, pentru a vă proteja datele cu caracter personal.

## 1.11 Drepturile dumneavoastră

În funcție de locația dumneavoastră, este posibil să aveți drepturi privind datele dumneavoastră cu caracter personal, inclusiv dreptul de acces, rectificare, ștergere, restricționare sau portabilitate a datelor și dreptul de opoziție față de anumite prelucrări. Consultați Secțiunea 3 (Declarația de Conformitate RGPD) pentru detalii complete despre modul de exercitare a acestor drepturi.

## 1.12 Modificări ale acestei Politici de Confidențialitate

Este posibil să actualizăm periodic această Politică de Confidențialitate. Vă vom notifica cu privire la modificările semnificative prin publicarea politicii actualizate în Aplicație sau pe site-ul nostru, împreună cu o dată de "Ultima actualizare" revizuită. Continuarea utilizării Aplicației după intrarea în vigoare a modificărilor constituie acceptarea politicii revizuite.

## 1.13 Contactați-ne

Dacă aveți întrebări sau nelămuriri cu privire la această Politică de Confidențialitate sau la practicile noastre privind datele, vă rugăm să ne contactați:

**FEEL IT SERVICES SAS (și filialele sale)**
13bis Avenue de la Motte Picquet, 75007 Paris, Franța
RCS Paris B 531 361 459
Telefon: +33 6 62 88 36 50
Email: connect@feel-it-services.com`;

const PRIVACY_CONTENT_FR = `## 1.1 Introduction

La présente Politique de confidentialité explique comment FEEL IT SERVICES SAS (et ses filiales) (« nous », « notre », « nos ») collecte, utilise, partage et protège les informations en lien avec l'application mobile Artefact (l'« Application »), disponible sur l'Apple App Store et le Google Play Store. En téléchargeant, en accédant à ou en utilisant l'Application, vous acceptez la collecte et l'utilisation des informations conformément à la présente Politique de confidentialité.

Si vous n'acceptez pas la présente Politique de confidentialité, veuillez ne pas utiliser l'Application.

## 1.2 Qui sommes-nous (responsable du traitement)

Le responsable du traitement de vos données personnelles est :

**FEEL IT SERVICES SAS (et ses filiales)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Téléphone : +33 6 62 88 36 50
E-mail : connect@feel-it-services.com

## 1.3 Informations que nous collectons

Nous collectons les catégories d'informations suivantes :

### a) Informations de compte

Lorsque vous créez un compte dans l'Application, nous pouvons collecter votre nom, votre adresse e-mail et votre mot de passe (chiffré) ou, si vous choisissez de vous connecter via un fournisseur tiers, un identifiant et des informations de profil de base provenant de ce fournisseur.

La création de compte est possible par e-mail et mot de passe, ou via une connexion avec Google ou Apple.

### b) Données de l'appareil photo et des photos

La fonctionnalité principale de l'Application vous permet de scanner des objets exposés et des œuvres d'art dans les musées à l'aide de l'appareil photo de votre appareil. Les images capturées ou téléversées à cette fin sont traitées afin d'identifier l'œuvre ou l'objet exposé et de vous fournir des informations associées (descriptions, faits historiques, parcours, etc.). L'accès à l'appareil photo n'est demandé que lorsque vous utilisez la fonction de scan et peut être géré à tout moment dans les réglages de votre appareil.

### c) Données de localisation

Avec votre autorisation, nous pouvons collecter des données de localisation approximatives ou précises afin de vous aider à trouver des musées, des expositions et des points d'intérêt à proximité, et de personnaliser votre parcours de visite.

Les données de localisation ne sont collectées que lorsque l'Application est en cours d'utilisation (premier plan) et ne font l'objet d'aucun suivi en arrière-plan.

### d) Données d'utilisation et d'analyse

Nous utilisons des services d'analyse tiers pour comprendre comment l'Application est utilisée (par exemple, écrans consultés, fonctionnalités utilisées, durée des sessions, rapports de plantage) afin d'améliorer les performances de l'Application et l'expérience utilisateur. Ces données sont généralement agrégées ou pseudonymisées.

### e) Informations sur l'appareil

Nous pouvons collecter automatiquement certaines informations techniques, telles que le modèle de l'appareil, le système d'exploitation et sa version, les identifiants uniques de l'appareil, les paramètres de langue et les informations sur le réseau mobile.

## 1.4 Fonctionnalités d'intelligence artificielle (IA)

L'Application utilise un service d'intelligence artificielle fourni par un tiers pour reconnaître et identifier les objets exposés et les œuvres d'art à partir des images que vous capturez. Ce processus de reconnaissance par IA analyse le contenu visuel de l'image (l'œuvre elle-même) afin de renvoyer des résultats d'identification et d'information.

Important : le fournisseur de reconnaissance par IA ne reçoit, ne traite ni ne conserve aucune donnée personnelle vous concernant. Seul le contenu de l'image nécessaire à la reconnaissance de l'objet ou de l'œuvre est transmis pour traitement, et il n'est pas associé par le fournisseur d'IA à votre identité, à votre compte ou à tout autre identifiant personnel.

## 1.5 Base juridique du traitement (utilisateurs de l'UE/EEE)

Lorsque le RGPD s'applique, nous traitons vos données personnelles sur les bases juridiques suivantes :

- Exécution d'un contrat — pour vous fournir les fonctionnalités principales de l'Application (compte, scan, parcours).
- Consentement — pour l'accès à l'appareil photo, la localisation précise et les fonctionnalités facultatives ; vous pouvez le retirer à tout moment.
- Intérêts légitimes — pour l'analyse, la sécurité, la prévention de la fraude et l'amélioration de l'Application, mis en balance avec vos droits.
- Obligation légale — lorsque le traitement est nécessaire pour respecter la législation applicable.

## 1.6 Comment nous partageons vos informations

Nous ne vendons pas vos données personnelles. Nous pouvons partager des informations avec :

- Les prestataires de services / sous-traitants qui fournissent des services pour notre compte, tels que l'hébergement cloud, l'analyse et la reconnaissance d'images par IA, dans le cadre d'obligations contractuelles de confidentialité et de protection des données.
- Les musées ou institutions culturelles, uniquement lorsque cela est nécessaire pour fournir le contenu des objets exposés, et généralement sous une forme agrégée ou non identifiable.
- Les autorités judiciaires et réglementaires, lorsque la loi, la réglementation ou une procédure judiciaire l'exige.
- Dans le cadre d'une opération sur le capital (fusion, acquisition, restructuration), sous réserve de garanties de confidentialité.

Les prestataires de services que nous utilisons sont : Google Analytics et Mixpanel pour les données d'utilisation et d'analyse, et OpenAI pour la reconnaissance des œuvres et des objets exposés par IA.

## 1.7 Conservation des données

Nous ne conservons les données personnelles que pendant la durée nécessaire aux finalités décrites dans la présente Politique de confidentialité, au respect de nos obligations légales, au règlement des litiges et à l'application de nos accords.

Plus précisément, nous conservons les données des utilisateurs pendant une durée de 1 an, à l'issue de laquelle elles sont supprimées ou anonymisées, sauf si une durée de conservation plus longue est requise pour respecter une obligation légale.

## 1.8 Sécurité des données

Nous mettons en œuvre des mesures techniques et organisationnelles appropriées destinées à protéger vos données personnelles contre tout accès, modification, divulgation ou destruction non autorisés. Toutefois, aucune méthode de transmission sur Internet ni de stockage électronique n'est sûre à 100 %, et nous ne pouvons garantir une sécurité absolue.

## 1.9 Protection des données des enfants

L'Application ne s'adresse pas aux enfants de moins de 16 ans. Nous ne collectons pas sciemment de données personnelles auprès d'enfants de moins de 16 ans. Si vous pensez qu'un enfant nous a fourni des données personnelles, veuillez nous contacter afin que nous puissions les supprimer.

## 1.10 Transferts internationaux de données

L'Application étant disponible à l'international, vos données peuvent être transférées et traitées dans des pays situés en dehors de votre pays de résidence, y compris en dehors de l'Espace économique européen (EEE), comme les États-Unis ou le Royaume-Uni. Lorsque de tels transferts ont lieu, nous nous appuyons sur des garanties appropriées, telles que les clauses contractuelles types (CCT) de la Commission européenne ou les décisions d'adéquation, pour protéger vos données personnelles.

## 1.11 Vos droits

Selon votre lieu de résidence, vous pouvez disposer de droits concernant vos données personnelles, notamment le droit d'accéder à vos données, de les rectifier, de les supprimer, d'en limiter le traitement ou d'en demander la portabilité, ainsi que le droit de vous opposer à certains traitements. Veuillez consulter la Section 3 (Déclaration de conformité au RGPD) pour savoir en détail comment exercer ces droits.

## 1.12 Modifications de la présente Politique de confidentialité

Nous pouvons mettre à jour la présente Politique de confidentialité de temps à autre. Nous vous informerons de toute modification importante en publiant la politique mise à jour dans l'Application ou sur notre site web, avec une date de « Dernière mise à jour » révisée. La poursuite de l'utilisation de l'Application après l'entrée en vigueur des modifications vaut acceptation de la politique révisée.

## 1.13 Nous contacter

Pour toute question ou préoccupation concernant la présente Politique de confidentialité ou nos pratiques en matière de données, veuillez nous contacter :

**FEEL IT SERVICES SAS (et ses filiales)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Téléphone : +33 6 62 88 36 50
E-mail : connect@feel-it-services.com`;

const PRIVACY_CONTENT_RU = `## 1.1 Введение

Настоящая Политика конфиденциальности объясняет, как FEEL IT SERVICES SAS (и её дочерние компании) («мы», «нас», «наш») собирает, использует, передаёт и защищает информацию в связи с мобильным приложением Artefact («Приложение»), доступным в Apple App Store и Google Play Store. Скачивая Приложение, получая к нему доступ или используя его, вы соглашаетесь на сбор и использование информации в соответствии с настоящей Политикой конфиденциальности.

Если вы не согласны с настоящей Политикой конфиденциальности, пожалуйста, не используйте Приложение.

## 1.2 Кто мы (оператор данных)

Оператором, ответственным за ваши персональные данные, является:

**FEEL IT SERVICES SAS (и её дочерние компании)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Телефон: +33 6 62 88 36 50
Эл. почта: connect@feel-it-services.com

## 1.3 Какую информацию мы собираем

Мы собираем следующие категории информации:

### a) Данные учётной записи

При создании учётной записи в Приложении мы можем собирать ваше имя, адрес электронной почты и пароль (в зашифрованном виде) либо, если вы решите войти через стороннего поставщика, идентификатор и основные данные профиля от этого поставщика.

Создать учётную запись можно с помощью электронной почты и пароля либо через вход с Google или Apple.

### b) Данные камеры и фотографий

Основная функция Приложения позволяет сканировать музейные экспонаты и произведения искусства с помощью камеры вашего устройства. Изображения, снятые или загруженные с этой целью, обрабатываются для идентификации произведения или экспоната и предоставления вам связанной информации (описаний, исторических фактов, маршрутов и т. д.). Доступ к камере запрашивается только при использовании функции сканирования и может быть изменён в любое время в настройках устройства.

### c) Данные о местоположении

С вашего разрешения мы можем собирать данные о приблизительном или точном местоположении, чтобы помочь вам найти ближайшие музеи, выставки и достопримечательности, а также персонализировать маршрут вашего посещения.

Данные о местоположении собираются только во время использования Приложения (в активном режиме) и не отслеживаются в фоновом режиме.

### d) Данные об использовании и аналитика

Мы используем сторонние сервисы аналитики, чтобы понимать, как используется Приложение (например, просмотренные экраны, используемые функции, продолжительность сессий, отчёты о сбоях), и улучшать производительность Приложения и пользовательский опыт. Эти данные, как правило, агрегируются или псевдонимизируются.

### e) Информация об устройстве

Мы можем автоматически собирать определённую техническую информацию, например модель устройства, операционную систему и её версию, уникальные идентификаторы устройства, языковые настройки и сведения о мобильной сети.

## 1.4 Функции искусственного интеллекта (ИИ)

Приложение использует сервис искусственного интеллекта, предоставляемый третьей стороной, для распознавания и идентификации музейных экспонатов и произведений искусства на снятых вами изображениях. Процесс распознавания с помощью ИИ анализирует визуальное содержание изображения (само произведение), чтобы вернуть результаты идентификации и информацию.

Важно: поставщик сервиса распознавания на основе ИИ не получает, не обрабатывает и не хранит никаких ваших персональных данных. Для обработки передаётся только содержание изображения, необходимое для распознавания объекта или произведения, и поставщик ИИ не связывает его с вашей личностью, учётной записью или любым другим персональным идентификатором.

## 1.5 Правовые основания обработки (пользователи из ЕС/ЕЭЗ)

В случаях, когда применяется GDPR, мы обрабатываем ваши персональные данные на следующих правовых основаниях:

- Исполнение договора — для предоставления основных функций Приложения (учётная запись, сканирование, маршруты).
- Согласие — для доступа к камере, точному местоположению и дополнительным функциям; вы можете отозвать его в любое время.
- Законные интересы — для аналитики, безопасности, предотвращения мошенничества и улучшения Приложения, с учётом соблюдения ваших прав.
- Юридическое обязательство — когда обработка необходима для соблюдения применимого законодательства.

## 1.6 Как мы передаём вашу информацию

Мы не продаём ваши персональные данные. Мы можем передавать информацию:

- Поставщикам услуг / обработчикам, оказывающим услуги от нашего имени, таким как облачный хостинг, аналитика и распознавание изображений на основе ИИ, в рамках договорных обязательств о конфиденциальности и защите данных.
- Музеям или учреждениям культуры — только когда это необходимо для предоставления контента об экспонатах и, как правило, в агрегированной или обезличенной форме.
- Судебным и регулирующим органам — когда этого требуют закон, нормативные акты или судебный процесс.
- В связи с корпоративной сделкой (слияние, поглощение, реорганизация) — при условии соблюдения мер по обеспечению конфиденциальности.

Мы используем следующих поставщиков услуг: Google Analytics и Mixpanel для данных об использовании и аналитики, а также OpenAI для распознавания произведений искусства и экспонатов с помощью ИИ.

## 1.7 Хранение данных

Мы храним персональные данные только в течение срока, необходимого для достижения целей, описанных в настоящей Политике конфиденциальности, выполнения юридических обязательств, урегулирования споров и обеспечения соблюдения наших соглашений.

В частности, мы храним данные пользователей в течение 1 года, после чего они удаляются или обезличиваются, если только более длительный срок хранения не требуется для выполнения юридического обязательства.

## 1.8 Безопасность данных

Мы применяем надлежащие технические и организационные меры, предназначенные для защиты ваших персональных данных от несанкционированного доступа, изменения, раскрытия или уничтожения. Однако ни один способ передачи данных через интернет или электронного хранения не является безопасным на 100 %, и мы не можем гарантировать абсолютную безопасность.

## 1.9 Конфиденциальность детей

Приложение не предназначено для детей младше 16 лет. Мы сознательно не собираем персональные данные детей младше 16 лет. Если вы считаете, что ребёнок предоставил нам персональные данные, пожалуйста, свяжитесь с нами, чтобы мы могли их удалить.

## 1.10 Международная передача данных

Поскольку Приложение доступно по всему миру, ваши данные могут передаваться и обрабатываться в странах за пределами страны вашего проживания, в том числе за пределами Европейской экономической зоны (ЕЭЗ), например в США или Великобритании. При такой передаче мы опираемся на надлежащие гарантии, такие как Стандартные договорные положения (SCC) Европейской комиссии или решения о достаточности уровня защиты, чтобы защитить ваши персональные данные.

## 1.11 Ваши права

В зависимости от вашего местонахождения у вас могут быть права в отношении ваших персональных данных, включая право на доступ, исправление, удаление, ограничение обработки или перенос данных, а также право возражать против определённых видов обработки. Подробнее о том, как реализовать эти права, см. в Разделе 3 (Заявление о соответствии GDPR).

## 1.12 Изменения настоящей Политики конфиденциальности

Мы можем время от времени обновлять настоящую Политику конфиденциальности. О существенных изменениях мы сообщим, опубликовав обновлённую политику в Приложении или на нашем сайте с новой датой «Последнего обновления». Продолжение использования Приложения после вступления изменений в силу означает принятие обновлённой политики.

## 1.13 Свяжитесь с нами

Если у вас есть вопросы или замечания относительно настоящей Политики конфиденциальности или нашей практики обращения с данными, пожалуйста, свяжитесь с нами:

**FEEL IT SERVICES SAS (и её дочерние компании)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Телефон: +33 6 62 88 36 50
Эл. почта: connect@feel-it-services.com`;

const PRIVACY_BLOCKS_EN = parseLegalContent(PRIVACY_CONTENT_EN);
const PRIVACY_BLOCKS_RO = parseLegalContent(PRIVACY_CONTENT_RO);
const PRIVACY_BLOCKS_FR = parseLegalContent(PRIVACY_CONTENT_FR);
const PRIVACY_BLOCKS_RU = parseLegalContent(PRIVACY_CONTENT_RU);

const PRIVACY_BLOCKS_BY_LANGUAGE = {
  ro: PRIVACY_BLOCKS_RO,
  fr: PRIVACY_BLOCKS_FR,
  ru: PRIVACY_BLOCKS_RU,
};

const PrivacyPolicyPage = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView('Privacy Policy Page');
  }, []);

  const blocks = useMemo(
    () => PRIVACY_BLOCKS_BY_LANGUAGE[i18n.resolvedLanguage] || PRIVACY_BLOCKS_EN,
    [i18n.resolvedLanguage]
  );

  return (
    <div className="privacy-policy-page">
      <LandingHeader />

      <main className="privacy-policy-page__main">
        <span className="privacy-policy-page__eyebrow">
          {t('landing.privacyPolicyPage.eyebrow', 'Legal')}
        </span>
        <h1 className="privacy-policy-page__title font-display">
          {t('landing.privacyPolicyPage.title', 'Privacy Policy')}
        </h1>
        <p className="privacy-policy-page__updated">
          {t('landing.privacyPolicyPage.lastUpdated', 'Last updated: August 21, 2026')}
        </p>

        <div className="privacy-policy-page__content">
          {blocks.map((block, index) => {
            if (block.type === 'heading') {
              return (
                <h2 key={index} className="privacy-policy-page__heading">
                  {block.text}
                </h2>
              );
            }

            if (block.type === 'subheading') {
              return (
                <h3 key={index} className="privacy-policy-page__subheading">
                  {block.text}
                </h3>
              );
            }

            if (block.type === 'list') {
              return (
                <ul key={index} className="privacy-policy-page__list">
                  {block.items.map((item, itemIndex) => (
                    <li key={itemIndex}>
                      {item.label && <strong>{item.label}: </strong>}
                      {item.text}
                    </li>
                  ))}
                </ul>
              );
            }

            return (
              <p key={index} className="privacy-policy-page__paragraph">
                {block.bold ? <strong>{block.text}</strong> : block.text}
              </p>
            );
          })}
        </div>
      </main>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default PrivacyPolicyPage;
